import { defaultConsentId } from '@lifeomic/phc-web-toolkit/src/components/ManageConsent/View/constants';
import { consentFormIdSystem } from '@lifeomic/phc-web-toolkit/src/util/reassignMemberConsent';

type QuestionnaireWithVersion = fhir.Questionnaire & { version?: string };

export interface AssignableConsent {
  title: string;
  consentFormId: string;
  questionnaireId: string;
}

const getConsentFormId = (questionnaire: fhir.Questionnaire) =>
  questionnaire.identifier?.find(id => id.system === consentFormIdSystem)
    ?.value;

const compareSemverLike = (a: string, b: string): number => {
  const left = a.split('.').map(part => parseInt(part, 10) || 0);
  const right = b.split('.').map(part => parseInt(part, 10) || 0);
  const length = Math.max(left.length, right.length);

  for (let index = 0; index < length; index++) {
    const leftValue = left[index] ?? 0;
    const rightValue = right[index] ?? 0;
    if (leftValue !== rightValue) {
      return leftValue < rightValue ? -1 : 1;
    }
  }

  return 0;
};

const getLatestQuestionnaire = (
  questionnaires: QuestionnaireWithVersion[],
  consentFormId: string,
) =>
  questionnaires
    .filter(questionnaire => questionnaire.status === 'active')
    .filter(questionnaire => getConsentFormId(questionnaire) === consentFormId)
    .reduce<QuestionnaireWithVersion | undefined>((latest, next) => {
      if (!latest) {
        return next;
      }
      if (!next.version || !latest.version) {
        return next.id && latest.id ? next : latest;
      }
      return compareSemverLike(next.version, latest.version) > 0
        ? next
        : latest;
    }, undefined);

const getAssignedFormIds = (directives: fhir.Consent[]) =>
  new Set(
    directives
      .flatMap(directive => {
        const identifier = directive.identifier as
          | fhir.Identifier
          | fhir.Identifier[]
          | undefined;
        return Array.isArray(identifier)
          ? identifier
          : identifier
          ? [identifier]
          : [];
      })
      .map(identifier => identifier.value)
      .filter(Boolean) as string[],
  );

const getAssignedQuestionnaireIds = (directives: fhir.Consent[]) =>
  new Set(
    directives
      .flatMap(directive => directive.meta?.tag || [])
      .map(tag => tag.code)
      .filter(Boolean) as string[],
  );

const isAssignableConsent = (
  consent: AssignableConsent | null,
): consent is AssignableConsent => consent !== null;

export const getUnassignedAssignableConsents = ({
  questionnaires,
  directives,
}: {
  questionnaires: fhir.Questionnaire[];
  directives: fhir.Consent[];
}): AssignableConsent[] => {
  const assignedFormIds = getAssignedFormIds(directives);
  const assignedQuestionnaireIds = getAssignedQuestionnaireIds(directives);
  const projectConsentFormIds = new Set(
    questionnaires
      .map(getConsentFormId)
      .filter(
        (consentFormId): consentFormId is string =>
          !!consentFormId && consentFormId !== defaultConsentId,
      ),
  );

  return Array.from(projectConsentFormIds)
    .map(projectConsentFormId => {
      const latestQuestionnaire = getLatestQuestionnaire(
        questionnaires,
        projectConsentFormId,
      );
      const consentFormId =
        latestQuestionnaire && getConsentFormId(latestQuestionnaire);

      if (!latestQuestionnaire?.id || !consentFormId) {
        return null;
      }

      if (
        assignedFormIds.has(consentFormId) ||
        assignedQuestionnaireIds.has(latestQuestionnaire.id)
      ) {
        return null;
      }

      return {
        title: latestQuestionnaire.title || consentFormId,
        consentFormId,
        questionnaireId: latestQuestionnaire.id,
      };
    })
    .filter(isAssignableConsent)
    .sort((left, right) => left.title.localeCompare(right.title));
};
